// Finansiell utvikling — inline-SVG area/line-chart over Brreg-regnskapsår.
// Sum gjeld (sienna, fylt areal) + egenkapital (sunset, linje). Paths genereres fra data
// (enkel ekvidistant x, lineær y-skala) — ingen chart-lib.
import { Skeleton } from "@/components/ui/skeleton";
import type { RegnskapAar } from "../api/types";

interface FinanceChartProps {
  regnskap: RegnskapAar[] | undefined;
  isLoading: boolean;
}

const W = 900;
const H = 200;
const TOP = 20; // topp-padding så toppunkt ikke klippes
const BOTTOM = 24; // plass til årstall-labels
const PLOT = H - TOP - BOTTOM;

interface Pt {
  x: number;
  y: number;
}

/** Lineær y: 0 → bunn av plottet, max → toppen. */
function scaleY(v: number, max: number): number {
  if (max <= 0) return TOP + PLOT;
  return TOP + PLOT - (v / max) * PLOT;
}

function linePath(pts: Pt[]): string {
  return pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
}

function areaPath(pts: Pt[]): string {
  if (pts.length < 2) return "";
  const base = TOP + PLOT;
  return `${linePath(pts)} L${pts[pts.length - 1].x.toFixed(1)},${base} L${pts[0].x.toFixed(1)},${base} Z`;
}

export function FinanceChart({ regnskap, isLoading }: FinanceChartProps) {
  return (
    <div className="glass px-6 py-[22px]">
      <h2 className="m-0 mb-4 flex justify-between text-[13px] font-semibold text-[var(--sub)]">
        Finansiell utvikling
        <span className="font-medium text-[var(--faint)]">Regnskapsregisteret</span>
      </h2>
      <div className="mb-3.5 flex gap-[18px] text-[12px] text-[var(--sub)]">
        <span>
          <i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[#C35A1A]" />
          Sum gjeld
        </span>
        <span>
          <i className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[#F28B2C]" />
          Egenkapital
        </span>
      </div>

      {isLoading ? (
        <Skeleton className="h-[200px] rounded-[14px] bg-[rgba(120,60,10,0.1)]" />
      ) : (
        <Chart regnskap={regnskap ?? []} />
      )}
    </div>
  );
}

function Chart({ regnskap }: { regnskap: RegnskapAar[] }) {
  const years = regnskap;
  if (years.length < 2) {
    return <p className="text-[13px] text-[var(--faint)]">For få regnskapsår til å tegne utvikling.</p>;
  }

  const max = Math.max(
    1,
    ...years.map((y) => y.sumGjeld ?? 0),
    ...years.map((y) => y.sumEgenkapital ?? 0)
  );
  const xOf = (i: number) => (i / (years.length - 1)) * W;

  const gjeld: Pt[] = years
    .map((y, i) => (y.sumGjeld == null ? null : { x: xOf(i), y: scaleY(y.sumGjeld, max) }))
    .filter((p): p is Pt => p != null);
  const ek: Pt[] = years
    .map((y, i) => (y.sumEgenkapital == null ? null : { x: xOf(i), y: scaleY(y.sumEgenkapital, max) }))
    .filter((p): p is Pt => p != null);

  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="si-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(195,90,26,.32)" />
          <stop offset="1" stopColor="rgba(242,139,44,0)" />
        </linearGradient>
      </defs>
      {gjeld.length >= 2 && <path fill="url(#si-area)" d={areaPath(gjeld)} />}
      {gjeld.length >= 2 && (
        <path fill="none" stroke="#C35A1A" strokeWidth="2.5" d={linePath(gjeld)} />
      )}
      {ek.length >= 2 && <path fill="none" stroke="#F28B2C" strokeWidth="2.5" d={linePath(ek)} />}
      <g fontSize="11" fill="#9c7a5d" fontFamily="-apple-system">
        {years.map((y, i) => {
          const x = xOf(i);
          const anchor = i === 0 ? "start" : i === years.length - 1 ? "end" : "middle";
          return (
            <text key={y.aar} x={x} y={H - 3} textAnchor={anchor}>
              {y.aar}
            </text>
          );
        })}
      </g>
    </svg>
  );
}
